import {AfterViewInit, Component, computed, ElementRef, inject, ViewChild} from '@angular/core';

import {ModalService} from '../../../../services/modal.service';

/**
 * Error Message Modal Component
 * Enhanced error message display with improved accessibility and better formatting
 * for different types of error messages (multi-line, file size errors, etc.)
 */
@Component({
  selector: 'app-error-message-modal',
  standalone: true,
  imports: [],
  templateUrl: './error-message-modal.component.html',
  styleUrl: './error-message-modal.component.scss'
})
export class ErrorMessageModalComponent implements AfterViewInit {
  // View child references for focus management
  @ViewChild('primaryButton') primaryButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('modalContent') modalContent?: ElementRef<HTMLDivElement>;
  private modalService = inject(ModalService);
  // Read-only signals from the modal service
  protected readonly showModal = this.modalService.showErrorMessageModal;
  protected readonly errorData = this.modalService.errorMessageData;
  /**
   * Computed signal that returns the formatted message
   * This helps handle multi-line messages and special formatting
   */
  protected readonly formattedMessage = computed(() => {
    return this.errorData()?.message || '';
  });

  /**
   * After view init lifecycle hook to set focus on the primary button
   */
  ngAfterViewInit(): void {
    // Set focus on the primary button when the modal opens
    if (this.primaryButton?.nativeElement) {
      setTimeout(() => {
        this.primaryButton?.nativeElement.focus();
      }, 100);
    }
  }

  /**
   * Close the error message modal
   */
  closeModal(): void {
    this.modalService.closeErrorMessageModal();
  }

  /**
   * Close the modal when clicking the overlay, but not when clicking the modal itself
   * @param event The click event
   */
  closeOnOverlayClick(event: MouseEvent): void {
    // Only close if the click was directly on the overlay (not its children)
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeModal();
    }
  }

  /**
   * Check if the message contains multiple lines
   */
  hasMultilineMessage(): boolean {
    const message = this.formattedMessage();
    return message.includes('\n');
  }

  /**
   * Check if this is a file size error message
   * This helps us apply special formatting for file size errors
   */
  isFileSizeError(): boolean {
    const message = this.formattedMessage();
    return message.includes('couldn\'t be added') ||
      message.includes('too large') ||
      message.includes('MB') ||
      message.includes('size limit');
  }

  /**
   * Get the introduction part of a file size error message
   * (the part before the list of files)
   */
  getErrorIntro(): string {
    const message = this.formattedMessage();

    // For file size errors, the intro is the first line
    if (message.includes('couldn\'t be added')) {
      const parts = message.split('\n');
      return parts[0];
    }

    // For other errors, return the whole message
    return message;
  }

  /**
   * Extract the list of error items from a file size error message
   */
  getErrorItems(): string[] {
    const message = this.formattedMessage();
    const lines = message.split('\n');

    // Skip the first line (intro) and the last line (limits)
    const errorItems = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      // Add lines that look like error items (containing parentheses)
      // but stop when we hit the limits line
      if (line.includes(')') && !line.includes('Limits:')) {
        errorItems.push(line);
      }
    }

    return errorItems;
  }

  /**
   * Extract the size limits information from a file size error
   */
  getSizeLimits(): string | null {
    const message = this.formattedMessage();
    const lines = message.split('\n');

    // Look for the line with limits information
    for (const line of lines) {
      if (line.includes('Limits:')) {
        return line;
      }
    }

    return null;
  }

  /**
   * Get all lines of the message for standard multi-line messages
   */
  getMessageLines(): string[] {
    return this.formattedMessage().split('\n');
  }
}
